// web/src/api.ts
// 后端 API 客户端。契约对齐《支付渠道解耦的后端框架设计》§7 统一 API 路由。
// 后端未部署/未联调时（阶段②），接口调用会失败，页面应降级提示"后端开发中"。

export interface ProductDto {
  key: string;
  prefix: string;
  nameZh: string;
  nameEn: string;
  emoji: string;
  priceCny: number;
  priceUsd: number;
}

export interface ChannelDto {
  key: string;
  nameZh: string;
  nameEn: string;
  enabled: boolean;
}

export interface CreateOrderRequest {
  /** 购买授权必填（一机一码）；免费软件打赏不需要 */
  machineCode?: string;
  productKey: string;
  channel: string;
  /** 打赏金额（分/美分）。传了 = 打赏流程（仅免费产品允许） */
  amount?: number;
}

export interface CheckoutResultDto {
  type: 'redirect' | 'qrcode' | 'form' | 'manual';
  url?: string;
  /** qrcode：二维码图片地址（如虎皮椒返回的 png）或纯文本内容，前端自动区分 */
  qrcode?: string;
  /** form：自提交 HTML（如面包多支付宝 WAP 表单） */
  html?: string;
  instructions?: string;
}

export interface CreateOrderResponse {
  ok: boolean;
  orderNo: string;
  /** purchase=购买授权；tip=免费软件打赏（付款后不发授权码，去下载页） */
  kind?: 'purchase' | 'tip';
  checkout: CheckoutResultDto;
}

export interface OrderStatusDto {
  ok: boolean;
  status: 'pending' | 'paid' | 'refunded' | 'expired';
  /** purchase=购买授权；tip=免费软件打赏（付款后无授权码，去下载页） */
  kind?: 'purchase' | 'tip';
  productKey?: string;
  licenseKey?: string;
}

export interface LookupResponse {
  ok: boolean;
  licenseKey?: string;
}

const API_BASE = ''; // 同源部署（Pages + Workers 同域或代理）；跨域时改为后端域名

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { 'content-type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = (body as { error?: string }).error || msg;
    } catch {
      /* 保留 HTTP 状态码信息 */
    }
    throw new Error(msg);
  }
  return (await res.json()) as T;
}

/** 后端下发的行（数据库字段名是下划线），前端内部一律用驼峰，所以这里统一转换一次 */
function pickString(row: Record<string, unknown>, camelName: string, snakeName = camelName): string {
  const value = row[camelName] ?? row[snakeName];
  return value === undefined || value === null ? '' : String(value);
}

function pickNumber(row: Record<string, unknown>, camelName: string, snakeName = camelName): number {
  const value = Number(row[camelName] ?? row[snakeName]);
  return Number.isFinite(value) ? value : 0;
}

/** 产品列表（服务端定价为准；失败时由调用方降级为本地 shared/products 展示） */
export function fetchProducts(): Promise<ProductDto[]> {
  return request<Array<Record<string, unknown>>>('/api/products').then((rows) =>
    rows.map((row) => ({
      key: pickString(row, 'key'),
      prefix: pickString(row, 'prefix'),
      nameZh: pickString(row, 'nameZh', 'name_zh'),
      nameEn: pickString(row, 'nameEn', 'name_en'),
      emoji: pickString(row, 'emoji'),
      priceCny: pickNumber(row, 'priceCny', 'price_cny'),
      priceUsd: pickNumber(row, 'priceUsd', 'price_usd'),
    })),
  );
}

/** 启用渠道列表 */
export function fetchChannels(): Promise<ChannelDto[]> {
  return request<Array<Record<string, unknown>>>('/api/channels').then((rows) =>
    rows.map((row) => ({
      key: pickString(row, 'key'),
      nameZh: pickString(row, 'nameZh', 'name_zh'),
      nameEn: pickString(row, 'nameEn', 'name_en'),
      // 数据库里是 0/1，接口也可能直接给布尔值
      enabled: row.enabled === 1 || row.enabled === true,
    })),
  );
}

/** 创建订单（一个产品一单） */
export function createOrder(req: CreateOrderRequest): Promise<CreateOrderResponse> {
  return request<CreateOrderResponse>('/api/orders', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** 订单状态（结果页轮询） */
export function fetchOrderStatus(orderNo: string): Promise<OrderStatusDto> {
  return request<OrderStatusDto>(`/api/orders/${encodeURIComponent(orderNo)}`);
}

/** 查询/补发授权码 */
export function lookupLicense(machineCode: string, productKey: string): Promise<LookupResponse> {
  return request<LookupResponse>(`/api/lookup?machineCode=${encodeURIComponent(machineCode)}&productKey=${encodeURIComponent(productKey)}`);
}
